/**
 * Clinical Analysis Service
 * Provides AI-powered clinical dermatology assessments for training samples.
 * Only available in staging environment.
 */

export interface ClinicalAnalysisChunk {
  id: number;
  text: string;
  timestamp: number;
}

export interface ClinicalAnalysisFinal {
  id: number;
  done: true;
  response: string;
  timestamp: number;
}

export interface ClinicalAnalysisError {
  id: -1;
  error: string;
  done: true;
  timestamp: number;
}

export type ClinicalAnalysisData = ClinicalAnalysisChunk | ClinicalAnalysisFinal | ClinicalAnalysisError;

/**
 * Stream clinical analysis for a training sample.
 * Yields chunks as they arrive from the server.
 * 
 * @param trainingSampleId - UUID of the training sample to analyze
 * @returns AsyncGenerator that yields analysis chunks
 * 
 * @example
 * ```typescript
 * try {
 *   for await (const chunk of streamClinicalAnalysis(sampleId)) {
 *     if ('done' in chunk && chunk.done) {
 *       if ('error' in chunk) {
 *         console.error('Error:', chunk.error);
 *       } else {
 *         console.log('Complete:', chunk.response);
 *       }
 *     } else {
 *       console.log('Chunk:', chunk.text);
 *     }
 *   }
 * } catch (error) {
 *   console.error('Stream failed:', error);
 * }
 * ```
 */
export async function* streamClinicalAnalysis(
  trainingSampleId: string
): AsyncGenerator<ClinicalAnalysisData, void, unknown> {
  const apiUrl = process.env.REACT_APP_BASE_URL || '';
  const endpoint = `${apiUrl}/staging/api/v2/training/utilities/clinical-analysis`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      training_sample_id: trainingSampleId,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6)) as ClinicalAnalysisData;
            yield data;
          } catch (error) {
            console.error('Failed to parse SSE data:', line, error);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Get complete clinical analysis for a training sample.
 * This function consumes the entire stream and returns the final result.
 * 
 * @param trainingSampleId - UUID of the training sample to analyze
 * @param onProgress - Optional callback for progress updates
 * @returns Promise with complete analysis
 * 
 * @example
 * ```typescript
 * const result = await getClinicalAnalysis(sampleId, (text) => {
 *   console.log('Progress:', text);
 * });
 * console.log('Final:', result.response);
 * ```
 */
export async function getClinicalAnalysis(
  trainingSampleId: string,
  onProgress?: (text: string, type: 'thinking' | 'response') => void
): Promise<{ response: string; thinking: string }> {
  let fullResponse = '';
  let fullThinking = '';

  for await (const chunk of streamClinicalAnalysis(trainingSampleId)) {
    if ('done' in chunk && chunk.done) {
      if ('error' in chunk) {
        throw new Error(chunk.error);
      }
      // Final chunk
      return {
        response: chunk.response,
        thinking: chunk.thinking,
      };
    } else {
      // Progress chunk
      if (chunk.type === 'response') {
        fullResponse += chunk.text;
      } else {
        fullThinking += chunk.text;
      }
      onProgress?.(chunk.text, chunk.type);
    }
  }

  // If we get here, stream ended without final chunk
  return {
    response: fullResponse,
    thinking: fullThinking,
  };
}
